import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
//@ts-ignore
import { Avatar, Badge } from 'dsm-react-lib'
import { getUserInitials } from '../../../../../utility/Utils'
import { IconSun, IconMoon, IconDeviceTablet } from '@tabler/icons-react'
import { ContributorIcon } from '../../../../../views/_pages/Chatbot/icons/Icons'
import { useNavigate } from 'react-router-dom'
import { releaseNotes } from '../../../../../configs/constants'
import ContributorModal from '../../../../../views/_templates/ContributorModal'
import { Role } from '../../../../../utility/types'
import { useThemeManager } from '../../../../../_hooks/useThemeManager'
import { createPortal } from 'react-dom'
import { IconBook2, IconNotebook, IconSettings, IconUserEdit, IconX } from '@tabler/icons-react'

const UserCard = ({ menuCollapsed }: any) => {
  const userCardRef = useRef(null)
  const avatarRef = useRef<HTMLDivElement>(null)
  const cardRef = useRef<HTMLDivElement>(null)
  const [isFullCard, setIsFullCard] = useState(false)
  const [cardPosition, setCardPosition] = useState({ top: 0, left: 0 })
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)
  const navigate = useNavigate()
  const { displayName, profilePictureBase64Encoded: imgUrl } = JSON.parse(
    localStorage.getItem('userInfoData') || '{}'
  )
  const {
    jobFunction: { name }
  } = useSelector((state: any) => state?.profileReducer?.profile)
  const [xHover, setXHover] = useState(false)
  const [contributorModal, setContributorModal] = useState(false)
  const { roles } = useSelector(
    (state: { userContextReducer: { userContext: { roles: string[] } } }) =>
      state.userContextReducer.userContext
  )
  const isContributor = roles?.includes(Role.Contributor)
  const isAdmin = roles?.includes(Role.Admin)
  const { theme, setTheme } = useThemeManager()

  // Track window width for responsive scaling
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const cardMenus = [
    // Show contributor option only for non-admin users
    ...(!isAdmin
      ? [
          {
            title: isContributor ? "You're a Contributor" : 'Contributor guide',
            icon: <ContributorIcon className="inline" color="var(--nav-icon)" />,
            onClick: () => setContributorModal(true)
          }
        ]
      : []),
    {
      title: 'Manage profile',
      icon: <IconUserEdit className="inline" color="var(--nav-icon)" />,
      onClick: () => navigate('/profile')
    },
    {
      title: 'About Centrali',
      icon: <IconBook2 size={18} color="var(--nav-icon)" />,
      onClick: () => navigate('/landing/home')
    },
    {
      title: 'Release notes',
      icon: <IconNotebook size={18} color="var(--nav-icon)" />,
      onClick: () => window.open(releaseNotes, '_blank')
    }
  ]

  useLayoutEffect(() => {
    if (userCardRef?.current && !menuCollapsed) {
      const cardHeight = (userCardRef.current as HTMLElement).offsetHeight
      document.documentElement.style.setProperty('--nav-menu-height', `${cardHeight + 90}px`)
    }
  }, [isFullCard, menuCollapsed])

  const themeOptions = [
    { title: 'Light', icon: <IconSun size={11} /> },
    { title: 'Dark', icon: <IconMoon size={11} /> },
    { title: 'System', icon: <IconDeviceTablet size={11} /> }
  ]

  const toggleTheme = (selectedTheme: string) => {
    setTheme(selectedTheme)
  }

  const viewFullUserCard = () => {
    setIsFullCard((prev) => !prev)
    setXHover(false)
  }

  // Calculate position for floating card when opened
  useEffect(() => {
    if (isFullCard && menuCollapsed && avatarRef.current && cardRef.current) {
      const avatarRect = avatarRef.current.getBoundingClientRect()
      const cardHeight = cardRef.current.offsetHeight

      setCardPosition({
        top: Math.max(8, avatarRect.bottom - cardHeight),
        left: avatarRect.right + 4
      })
    }
  }, [isFullCard, menuCollapsed])

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (
        isFullCard &&
        avatarRef.current &&
        !avatarRef.current.contains(e.target as Node) &&
        cardRef.current &&
        !cardRef.current.contains(e.target as Node)
      ) {
        setIsFullCard(false)
      }
    }
    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [isFullCard])

  // Close full card when menu collapses
  useEffect(() => {
    if (menuCollapsed) {
      setIsFullCard(false)
    }
  }, [menuCollapsed])

  const getAvatar = (size: number) => (
    <Avatar
      content={imgUrl ? imgUrl : getUserInitials(displayName)}
      {...(imgUrl ? {} : { bgColor: 'light-primary' })}
      size={`${size}px`}
      className="flex-shrink-0"
    />
  )

  const renderCardHeader = () => (
    <>
      <div className="flex items-center justify-between space-x-3">
        <div
          className="w-[48px] h-[48px] theme-surface rounded-full flex items-center justify-center mt-[-25px]"
          onClick={viewFullUserCard}>
          {getAvatar(48)}
        </div>
        <div className="flex items-center gap-[8px]">
          {(isAdmin || isContributor) && (
            <Badge
              className={
                isAdmin ? 'team-role-badge team-role-badge-owner' : 'theme-badge-muted-pill'
              }
              size="small"
              label={isAdmin ? 'Admin' : isContributor ? 'Contributor' : ''}
            />
          )}
          {isFullCard ? (
            <IconX
              onClick={viewFullUserCard}
              className="pointer focus:outline-none"
              size={18}
              onMouseEnter={() => setXHover(true)}
              onMouseLeave={() => setXHover(false)}
              tabIndex={0}
              color={xHover ? 'var(--color-primary)' : 'var(--nav-icon)'}
            />
          ) : (
            !menuCollapsed && (
              <IconSettings
                data-testid="assistant-settings"
                onClick={viewFullUserCard}
                className="pointer"
                color="var(--nav-icon)"
              />
            )
          )}
        </div>
      </div>
      <div className="pt-[8px] pl-[8px]">
        <div className="theme-text-heading py-[4px] text-[16px] font-[600]">{displayName}</div>
        <div className="theme-text-subheading">{name}</div>
      </div>
    </>
  )

  const renderFullCardContent = () => (
    <>
      <hr className="theme-border-default my-[2px] mt-[6px]" />
      {/* Menu Items */}
      <div className="flex flex-col my-1">
        {cardMenus.map((menu) => (
          <div
            key={menu.title}
            className="theme-interactive-item justify-start items-center space-x-2 cursor-pointer p-[6px] rounded"
            onClick={menu.onClick}>
            {menu.icon}
            <span className="text-sm">{menu.title}</span>
          </div>
        ))}
      </div>
      <hr className="theme-border-default my-[2px]" />
      <div className="px-[8px] py-[6px]">
        <h4 className="theme-text-heading font-[600] mb-[6px] text-sm">Theme</h4>
        <div className="theme-segmented-control flex items-center rounded-[6px] overflow-hidden border">
          {themeOptions.map((option, index) => {
            const isSelected = theme === option.title.toLowerCase()

            return (
              <button
                key={option.title}
                aria-label={`Set theme to ${option.title}`}
                aria-pressed={isSelected}
                className={`theme-segmented-option flex items-center justify-center gap-[3px] flex-1 min-w-0 py-[6px] px-[4px] caption-text leading-none transition-all duration-150 border-0 ${
                  isSelected ? 'theme-segmented-option-active' : 'theme-segmented-option-inactive'
                } ${
                  index !== themeOptions.length - 1 ? 'theme-segmented-option-divider border-r' : ''
                }`}
                onClick={() => toggleTheme(option.title.toLowerCase())}>
                <span className="inline-flex items-center opacity-95 shrink-0">{option.icon}</span>
                <span className="font-[600] truncate">{option.title}</span>
              </button>
            )
          })}
        </div>
      </div>
    </>
  )

  const renderUserCard = () => (
    <div className="user-profile-card theme-overlay-card p-[12px] pt-[10px] absolute bottom-4 left-4 right-4">
      {renderCardHeader()}
      {isFullCard && renderFullCardContent()}
    </div>
  )

  return (
    <>
      {!menuCollapsed ? (
        <div ref={userCardRef}>{renderUserCard()}</div>
      ) : (
        <div ref={userCardRef} className="absolute bottom-10 left-4 right-4">
          <div ref={avatarRef} className="cursor-pointer" onClick={viewFullUserCard}>
            {getAvatar(32)}
          </div>
          {isFullCard &&
            createPortal(
              <div
                ref={cardRef}
                className="fixed w-[260px] p-[12px] pt-[10px] z-[9999] bg-[var(--surface-bg)] border border-[var(--gray-300)] rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.1)]"
                // Inline styles needed for portaled elements - Tailwind classes don't apply reliably to createPortal
                style={{
                  top: `${cardPosition.top}px`,
                  left: `${cardPosition.left}px`,
                  transform: windowWidth <= 1650 ? 'scale(0.75)' : 'scale(1)',
                  transformOrigin: 'left bottom'
                }}>
                {renderCardHeader()}
                {renderFullCardContent()}
              </div>,
              document.body
            )}
        </div>
      )}
      {contributorModal && (
        <ContributorModal
          isOpen={contributorModal}
          toggle={() => setContributorModal(false)}
          handleToggleArcRequestModal={() => setContributorModal(false)}
          isContributor={isContributor}
        />
      )}
    </>
  )
}

export default UserCard
